import React, { useEffect, useRef, useState } from "react";
import { MultiSelect } from "@progress/kendo-react-dropdowns";
import { FloatingLabel } from "@progress/kendo-react-labels";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { icon, label } from "../../../config";
import "./selector.scss";

/**
 * common component used for rendering multiselects on pages for filters
 * @param {*} props
 * @author {Prachi Jain}
 */
const PageFilterMultiselect = (props) => {
  const {
    multiSelectData = [],
    placeholder,
    selectedData = [],
    filterKey,
    filterSelections,
    setFilterSelections,
    showPlaceHolder,
    tagsLength,
    listNoDataRender,
    onFilterValueChange,
    autoFocus,
    disabled = false,
  } = props;
  const [selectAll, setSelectAll] = useState(false);
  const [allDataList, setAllDataList] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const multiSelectRef = useRef(null);

  useEffect(() => {
    setAllDataList([{ label: "Select All" }, ...multiSelectData]);
  }, [multiSelectData]);

  useEffect(() => {
    const isAllFieldsSelected = selectedData.filter((item) => item.value)?.length === multiSelectData.filter((item) => item.value)?.length;
    setSelectAll(isAllFieldsSelected);
  }, [selectedData]);

  useEffect(() => {
    if (autoFocus && multiSelectRef?.current && typeof multiSelectRef.current.focus === "function") {
      multiSelectRef.current.focus();
    }
  }, [autoFocus]);

  /**
   * isChecked function is used to show checkbox selected on options which are selected
   * it does checking values in state which contains selected values of mutliselect
   * @param {Event} e
   */
  const isChecked = (id) => {
    const validElements = selectedData.map((item) => item.value);
    return validElements.includes(id);
  };

  /**
   * customized tags for respective filters
   * @param {Object} tagData
   * @param {Object} li
   * @return {JSX Element}
   */
  const tagRender = (tagData, li) => {
    if (selectedData.length > tagsLength) {
      if (tagData?.data[0] === selectedData[0]) {
        const selectedFilters = selectedData.map((item) => item.label).join(", ");
        return React.cloneElement(li, li.props, [
          <span id='filter-all-tags' title={selectedFilters} onClick={() => setIsDropdownOpen(true)}>
            {`${selectedData.length} Selected`}
          </span>,
        ]);
      }
      return null;
    } else {
      const tagInfo = tagData?.data[0];
      return React.cloneElement(li, li.props, [
        <span
          className={"custom-tag tag-width text-truncate"}
          onMouseEnter={li.props.onMouseEnter}
          onMouseLeave={li.props.onMouseLeave}
          title={tagInfo?.label}
          id='filter-all-tags'>
          {tagInfo?.labelModified ?? tagInfo?.label}
          {li.props.onRemove && (
            <span
              className='remove-search-button d-flex justify-content-center align-items-center position-absolute cursor-pointer'
              onClick={() => li.props.onRemove(tagData)}
              id='filter-tag-cross-icon'
              title={label.REMOVE}>
              {icon.CLEAR}
            </span>
          )}
        </span>,
        li.props.children,
      ]);
    }
  };

  /**
   * itemRender function is used to render element coressponding to each option in MultiSelect
   * itemRender is used so that the each elment option is customizable
   * (so that we are able to include checkbox and  in element option)
   **/
  const itemRender = (li, itemProps) => {
    const { dataItem } = itemProps;

    const itemChildren = (
      <span className='filter-multiselect-container d-flex justify-content-between' title={dataItem?.label}>
        <div className="d-flex w-100">
          <input
            className='mr-2 mt-1'
            id={`filter-multi-select-popup-checkbox-${dataItem?.value}`}
            type='checkbox'
            checked={dataItem?.label === label.SELECT_ALL ? selectAll : isChecked(dataItem?.value)}
            onChange={() => {}}
          />
          <div className="text-truncate">
            <span className='assignee-name text-truncate'>{dataItem?.label}</span>
            {dataItem?.email && (
              <span className='assignee-email text-truncate' style={{ display: "block", fontSize: "12px", color: "#888" }}>
                {dataItem.email}
              </span>
            )}
          </div>
        </div>
      </span>
    );
    return React.cloneElement(li, li.props, itemChildren);
  };

  const selectAllClickHandler = (isAllFieldsSelected, setSelectAll) => {
    if (isAllFieldsSelected) {
      setFilterSelections({ ...filterSelections, [filterKey]: "" });
      setSelectAll(false);
      return;
    }

    setSelectAll(true);

    const allValues = multiSelectData?.map((item) => item.value).join(",");
    setFilterSelections({ ...filterSelections, [filterKey]: allValues });
  };

  /**
   * handles change of multiselect
   * @param {*} event
   */
  const handleMultiselectValues = (event) => {
    const updatedSelectedData = event.value;
    const isAllFieldsSelected = updatedSelectedData.filter((item) => item.value)?.length === multiSelectData.filter((item) => item.value)?.length;
    setSelectAll(isAllFieldsSelected);

    if (updatedSelectedData?.find((item) => item.label === label.SELECT_ALL)?.label) {
      selectAllClickHandler(isAllFieldsSelected, setSelectAll);
      return;
    }

    const allValues = updatedSelectedData?.map((item) => item.value).join(",");
    setFilterSelections({ ...filterSelections, [filterKey]: allValues });
  };

  const filterChange = (event) => {
    const filterValue = event.filter.value.toString().toLowerCase();
    const filteredData = multiSelectData?.filter((arrItem) => {
      const labelMatch = arrItem?.label?.toLowerCase()?.includes(filterValue);
      const emailMatch = arrItem?.email ? arrItem?.email?.toLowerCase()?.includes(filterValue) : false;
      return labelMatch || emailMatch;
    });
    setAllDataList(filteredData);
    if (onFilterValueChange) {
      onFilterValueChange(filterValue);
    }
  };

  return (
    <div className='queue-filter filter-tags'>
      <Tooltip anchorElement='target' parentTitle={true} position='bottom'>
        {showPlaceHolder ? (
          <FloatingLabel label={placeholder} className='small w-100' editorValue={selectedData?.length ? selectedData : ""}>
            <MultiSelect
              id={`filter-multi-select`}
              className='multi-select'
              data={allDataList}
              onOpen={() => setIsDropdownOpen(true)}
              onClose={() => setIsDropdownOpen(false)}
              onSelect={() => {
                setIsDropdownOpen(true);
              }}
              filterable={true}
              value={selectedData}
              autoClose={false}
              textField='label'
              dataItemKey='value'
              itemRender={itemRender}
              onChange={handleMultiselectValues}
              onFilterChange={filterChange}
              placeholder={""}
              popupSettings={{ className: "project-filter-list" }}
              tagRender={tagRender}
              opened={isDropdownOpen}
              listNoDataRender={listNoDataRender}
              ref={multiSelectRef}
              disabled={disabled}
            />
          </FloatingLabel>
        ) : (
          <MultiSelect
            id={`filter-multi-select`}
            className='multi-select'
            data={allDataList}
            onOpen={() => setIsDropdownOpen(true)}
            onClose={() => setIsDropdownOpen(false)}
            onSelect={() => {
              setIsDropdownOpen(true);
            }}
            filterable={true}
            value={selectedData}
            autoClose={false}
            textField='label'
            dataItemKey='value'
            itemRender={itemRender}
            onChange={handleMultiselectValues}
            onFilterChange={filterChange}
            placeholder={placeholder}
            popupSettings={{ className: "project-filter-list" }}
            tagRender={tagRender}
            opened={isDropdownOpen}
            listNoDataRender={listNoDataRender}
            ref={multiSelectRef}
            disabled={disabled}
          />
        )}
      </Tooltip>
    </div>
  );
};

export default PageFilterMultiselect;
